<template>
  <manual-page
    title="Gestion de Alimentos"
    description="Guia completa para administrar categorias de alimento, plantillas de dieta y frecuencias de entrega."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'dietCategoriesIndex' }"
    action-text="Ver categorias de alimento"
  >
    <!-- Overview -->
    <h2>Vista General del Modulo</h2>
    <p>
      El modulo de gestion de alimentos es el corazon del catalogo de productos de Trunimal.
      Aqui defines los tipos de alimento disponibles, sus variantes, y las plantillas
      que se usan para calcular porciones y precios en las cotizaciones.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Este modulo incluye:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Categorias de alimento:</strong> Tipos principales (BARF, cocido, mixto)</li>
        <li><strong>Subcategorias:</strong> Variantes y sabores dentro de cada categoria</li>
        <li><strong>Plantillas de dieta:</strong> Configuraciones de porciones y precios</li>
        <li><strong>Frecuencias:</strong> Opciones de periodicidad de entrega</li>
      </ul>
    </div>

    <!-- Categories Section -->
    <h2>Categorias de Alimento</h2>
    <p>
      Las categorias son los tipos principales de alimento que ofrece Trunimal.
      Cada categoria representa una forma diferente de preparacion y alimentacion.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tipos de categorias</h3>
          <p class="text-gray-600 mb-2">Las categorias comunes incluyen:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>BARF:</strong> Alimento crudo biologicamente apropiado</li>
            <li><strong>Cocido:</strong> Alimento natural cocido</li>
            <li><strong>Mixto:</strong> Combinacion de crudo y cocido</li>
            <li><strong>Especial:</strong> Formulas para necesidades especificas</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion de categoria</h3>
          <p class="text-gray-600 mb-2">Cada categoria define:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Identificador del tipo de alimento</li>
            <li><strong>Descripcion:</strong> Explicacion de beneficios y caracteristicas</li>
            <li><strong>Especie:</strong> Perro, gato o ambos</li>
            <li><strong>Estado:</strong> Activo o inactivo en el catalogo</li>
          </ul>
        </div>
      </div>
    </div>

    <h3>Crear una Categoria</h3>
    <p>Para agregar una nueva categoria de alimento:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al formulario</h3>
          <p class="text-gray-600">
            Ve a Alimentos y haz clic en "Crear categoria".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Completar informacion</h3>
          <p class="text-gray-600">
            Define el nombre, descripcion detallada, y selecciona para que especies
            esta disponible (perro, gato o ambos).
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Configurar caracteristicas</h3>
          <p class="text-gray-600">
            Agrega las caracteristicas especiales, beneficios e indicaciones
            que apareceran en las cotizaciones para los clientes.
          </p>
        </div>
      </div>
    </div>

    <!-- Subcategories Section -->
    <h2>Subcategorias</h2>
    <p>
      Las subcategorias son las variantes dentro de cada categoria. Permiten ofrecer
      diferentes opciones de sabor, formula o presentacion.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Ejemplos de subcategorias:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Por sabor:</strong> Res, pollo, pavo, mixto</li>
        <li><strong>Por formula:</strong> Regular, light, senior, puppy</li>
        <li><strong>Por presentacion:</strong> Con hueso, sin hueso, molido</li>
        <li><strong>Por condicion:</strong> Hipoalergenico, bajo en grasa</li>
      </ul>
    </div>

    <p>
      Las subcategorias se crean desde el detalle de cada categoria. Cada subcategoria
      puede tener su propia configuracion de precio y disponibilidad.
    </p>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Usa subcategorias para ofrecer variedad sin crear
        demasiadas categorias principales. Esto mantiene el catalogo organizado.
      </p>
    </div>

    <!-- Diet Templates Section -->
    <h2>Plantillas de Dieta</h2>
    <p>
      Las plantillas de dieta son configuraciones predefinidas que determinan
      como se calculan las porciones y precios para cada tipo de mascota.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Proposito de las plantillas</h3>
          <p class="text-gray-600 mb-2">Las plantillas sirven para:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Definir la composicion del alimento (ingredientes y proporciones)</li>
            <li>Establecer porciones segun el peso de la mascota</li>
            <li>Calcular precios automaticamente</li>
            <li>Estandarizar las cotizaciones</li>
          </ul>
        </div>
      </div>
    </div>

    <h3>Ver Plantillas</h3>
    <p>
      La lista de plantillas muestra todas las configuraciones disponibles.
      Puedes filtrar por categoria o estado.
    </p>

    <h3>Crear una Plantilla</h3>
    <p>Para crear una nueva plantilla de dieta:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion basica</h3>
          <p class="text-gray-600">
            Haz clic en "Crear plantilla" y selecciona la categoria base.
            Asigna un nombre descriptivo a la plantilla.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Definir ingredientes</h3>
          <p class="text-gray-600">
            Agrega los ingredientes que componen esta dieta y sus proporciones.
            Esto es informativo y puede mostrarse al cliente.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Configurar porciones</h3>
          <p class="text-gray-600">
            Define la tabla de porciones segun el peso de la mascota.
            Por ejemplo: mascotas de 5-10 kg reciben X gramos diarios.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Establecer precios</h3>
          <p class="text-gray-600">
            Define el precio base por kilogramo o por porcion. El sistema
            calculara automaticamente el precio final segun la cantidad.
          </p>
        </div>
      </div>
    </div>

    <!-- Template Configuration -->
    <h2>Configuracion Avanzada de Plantillas</h2>
    <p>
      Cada plantilla puede tener configuraciones adicionales para manejar
      casos especiales y ajustes.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Opciones de configuracion:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Factores de ajuste:</strong> Modificadores por condiciones especiales (sobrepeso, actividad alta)</li>
        <li><strong>Minimos y maximos:</strong> Limites de porciones por seguridad</li>
        <li><strong>Descuentos por volumen:</strong> Precios especiales por cantidad</li>
        <li><strong>Disponibilidad:</strong> Restricciones por sucursal o temporada</li>
      </ul>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Los cambios en las plantillas afectan las nuevas cotizaciones,
        pero no modifican los planes ya activos.
      </p>
    </div>

    <!-- Frequencies Section -->
    <h2>Frecuencias de Entrega</h2>
    <p>
      Las frecuencias determinan cada cuanto tiempo se genera una nueva orden
      y se entrega alimento al cliente.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Frecuencias disponibles</h3>
          <p class="text-gray-600 mb-2">Las opciones tipicas son:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Semanal:</strong> Entrega cada 7 dias - ideal para alimento fresco</li>
            <li><strong>Bisemanal:</strong> Cada 14 dias - balance entre frescura y conveniencia</li>
            <li><strong>Mensual:</strong> Cada 30 dias - para quienes prefieren menos entregas</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Impacto en el plan</h3>
          <p class="text-gray-600">
            La frecuencia afecta la cantidad de alimento por entrega y el precio total.
            Entregas mas frecuentes significan porciones mas pequeñas pero mas frescas.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> La frecuencia semanal es ideal para mascotas pequeñas,
        mientras que la mensual funciona mejor para mascotas grandes con mayor capacidad de almacenamiento.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Practicas</h2>
    <ul>
      <li><strong>Mantener el catalogo actualizado:</strong> Revisa periodicamente las categorias y subcategorias activas</li>
      <li><strong>Revisar precios regularmente:</strong> Ajusta las plantillas cuando cambien los costos de produccion</li>
      <li><strong>Documentar cambios:</strong> Registra por que se modifican las plantillas</li>
      <li><strong>Probar antes de activar:</strong> Verifica que las nuevas configuraciones calculen correctamente</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualDietManagement",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualQuoteRequests", label: "Solicitudes de cotizacion" },
        { route: "manualPlans", label: "Gestion de planes" },
        { route: "manualOrders", label: "Ordenes" },
        { route: "manualStore", label: "Tienda" },
      ],
    };
  },
};
</script>
